import React, { useState, useEffect, FC } from 'react';
import { Grid, Container } from '@material-ui/core';
import {
  BoxV2 as Box,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
} from 'portal-commons';

import { globalGetService } from '@/utils/globalApiServices';
import { Loader, FilterToggleButton } from '@/shared_elements';
import EventsListingRow from '../components/EventsListingRow';
import { getAllEventTypes } from '../apiServices';
import '../assets/events-listing-module.scss';
import { MAX_EVENT_RECORDS } from '../constants';
import { EventData, EventType } from '../types';
import { convertTimeWithTimezone } from '@/utils/time';
import { getAuthenticatedUserSuggestions } from '@/apis/services';
import { constantizeAuthenticatedUser } from '@/utils/string';

const FILTER_CONFIG = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter Campaign ID',
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA Name',
    suggestions: [],
  },
  eventType: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Event Type',
  },
  authenticatedUser: {
    type: FilterType.Text,
    label: 'User',
    placeholder: 'Enter User',
    suggestions: [],
    width: 250,
  },
};

const headRows = [
  { label: 'EVENT TYPE', id: 'eventType', sortable: false },
  { label: 'CATEGORY', id: 'categoryType', sortable: false },
  { label: 'CAMPAIGN ID', id: 'campaignUid', sortable: false },
  { label: 'BRAND ID', id: 'brandUid', sortable: false },
  { label: 'DATE & TIME', id: 'createDate', sortable: false },
  { label: 'USER', id: 'authenticatedUser', sortable: false },
  { label: 'DESCRIPTION', id: 'description', sortable: false },
];

const fetchBrandSuggestions = (query = {}) =>
  globalGetService('mno/brands/suggestions', { ...query, limit: 20 }).then(
    (response) =>
      response?.ok
        ? response.data?.map(
            ({ brandName }: { brandName: string }) => brandName
          ) ?? []
        : []
  );

const fetchCspSuggestions = (query = {}) =>
  globalGetService('mno/csp/suggestions', { ...query, limit: 20 }).then(
    (response) =>
      response?.ok
        ? response.data?.map(({ cspName }: { cspName: string }) => cspName) ??
          []
        : []
  );

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => fetchBrandSuggestions({ prefix: value }),
    },
    cspName: {
      minLength: 2,
      load: (value: string) => fetchCspSuggestions({ prefix: value }),
    },
    authenticatedUser: {
      minLength: 2,
      load: (value: string) => getAuthenticatedUserSuggestions(value),
    },
  },
};

const EventsUS: FC = () => {
  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const {
    appliedFilters,
    configs: filterConfigs,
    additionalFilters,
    candidateFilters,
    handleEdit: handleCandidateFiltersChange,
    handleApply: handleAppliedFiltersChange,
    setOptions,
  } = filter;

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const table = useTable<EventData>(
    async (filters) => {
      const page = filters?.page || 1;
      const authenticatedUser = constantizeAuthenticatedUser(
        appliedFilters.authenticatedUser
      );
      const query = {
        page,
        ...additionalFilters,
        ...appliedFilters,
        ...(authenticatedUser ? { authenticatedUser } : {}),
      };
      const response = await globalGetService('mno/event', query).then(
        (response) => (response?.ok ? response?.data : null)
      );
      return {
        data:
          response?.records?.map((r: EventData) => ({
            ...r,
            createDate: convertTimeWithTimezone(r.createDate),
          })) || [],
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || 10,
        total: response?.totalRecords || 0,
      };
    },
    {
      cappingTotal: MAX_EVENT_RECORDS,
      deps: [appliedFilters, additionalFilters],
    }
  );

  const fetchFilterOptions = async () => {
    const eventTypes = await getAllEventTypes();
    setOptions(
      'eventType',
      eventTypes.map((item: EventType) => ({
        label: item.id,
        value: item.id,
      }))
    );
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  return (
    <section
      className="events-listing-section page-content"
      data-testid="events"
    >
      {table.pageLoading ? (
        <Loader />
      ) : (
        <Container maxWidth={false} className="events-listing-container">
          <Box>
            <div className="title-block">
              <h2>{!table.pageLoading ? `${table.total} Events` : 'Events'}</h2>
              <span>
                This page can only show the latest{' '}
                {MAX_EVENT_RECORDS.toLocaleString('en-US')} records.Contact
                support for details on your remaining records.
              </span>
            </div>
          </Box>
          <Box sx={{ flexDirection: 'column', mt: 's', mb: 's' }}>
            <Box>
              <FilterToggleButton
                visible={isFilterVisible}
                onClick={toggleFilterVisibility}
              />
            </Box>
            {isFilterVisible && (
              <TableFilter
                portal="mno"
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={handleCandidateFiltersChange}
                onAppliedValuesChange={handleAppliedFiltersChange}
                className="filters"
                data-testid="events"
              />
            )}
          </Box>
          <Grid container justifyContent="center" spacing={0}>
            <Table
              headRows={headRows}
              emptyState="no events to view"
              loading={table.loading}
              tableData={table.tableData.map((record, index) => (
                <EventsListingRow key={index} event={record} />
              ))}
              handleChangePage={table.handleChangePage}
              createSortHandler={table.handleSorting}
              filter={table.filter}
              pagination={table.pagination}
            />
          </Grid>
        </Container>
      )}
    </section>
  );
};

export default EventsUS;
